import { useEffect, useState } from 'react';
import { Drawer as DrawerPrimitive } from '@base-ui/react/drawer';
import { XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/**
 * How a dialog or a sheet sits on a phone (spec 261).
 *
 * - `sheet`: rises from the bottom edge, as tall as its content, at most the
 *   screen less a strip of the page above it. A question, a short form.
 * - `full`: rises from the bottom edge to the top. A place: the task, a long
 *   form, a flow with steps.
 * - `keep`: as on a desktop. Only for what is already shaped for a phone, the
 *   navigation drawer that slides from the left.
 */
export type PhoneLayout = 'sheet' | 'full' | 'keep';

/**
 * A dialog or a sheet on a phone: a bottom sheet (spec 261).
 *
 * Kept in `components/ui/phone/`, outside the desktop class record
 * (`tests/shared/desktopClasses.test.ts`): nothing here is drawn above 767px.
 *
 * Built on Base UI's drawer, whose root `ui/dialog` and `ui/sheet` switch to on
 * a phone. The drawer is Base UI's dialog with a swipe added, so the focus trap,
 * Escape, the scroll lock and every Title, Description and Close inside work as
 * they do in a dialog; this adds the swipe down to dismiss, the handle that
 * says it can be swiped, and the motion of an edge-anchored surface.
 *
 * The popup scrolls as a whole, so a footer pinned to its bottom edge
 * (`DialogFooter`, `SheetFooter`) stays in reach on a form longer than the
 * screen, and a swipe starting on the content dismisses only once that content
 * is scrolled to its top.
 */
export function BottomSheetPopup({
  layout,
  slot,
  closeClassName,
  showCloseButton,
  className,
  children,
  ...props
}: DrawerPrimitive.Popup.Props & {
  layout: Exclude<PhoneLayout, 'keep'>;
  /** The `data-slot` the desktop popup carries, so callers and tests find either. */
  slot: string;
  closeClassName: string;
  showCloseButton: boolean;
}) {
  return (
    <DrawerPrimitive.Portal>
      <DrawerPrimitive.Backdrop
        data-slot={`${slot}-overlay`}
        className="fixed inset-0 isolate z-50 bg-black/30 opacity-[calc(1-var(--drawer-swipe-progress))] transition-opacity duration-[var(--motion-duration-slow)] ease-[var(--motion-ease-standard)] data-starting-style:opacity-0 data-ending-style:opacity-0 data-ending-style:duration-[var(--motion-duration-base)] data-ending-style:ease-[var(--motion-ease-exit)] data-swiping:duration-0"
      />
      <DrawerPrimitive.Viewport className="fixed inset-0 z-50 flex items-end justify-center">
        <DrawerPrimitive.Popup
          data-slot={slot}
          data-phone-layout={layout}
          className={cn(
            'relative flex w-full flex-col overflow-y-auto overscroll-contain rounded-t-2xl bg-popover text-sm text-popover-foreground shadow-floating ring-1 ring-foreground/10 outline-none',
            // Follows the finger while swiped, then rises and falls the height
            // of itself: the slow/base pair a sheet uses, as on a desktop.
            '[transform:translateY(var(--drawer-swipe-movement-y))] transition-transform duration-[var(--motion-duration-slow)] ease-[var(--motion-ease-standard)] data-starting-style:[transform:translateY(100%)] data-ending-style:[transform:translateY(100%)] data-ending-style:duration-[var(--motion-duration-base)] data-ending-style:ease-[var(--motion-ease-exit)] data-swiping:transition-none data-swiping:select-none',
            className,
            // The sheet's own geometry, last, so a desktop size or offset the
            // caller set (`sm:max-w-md`, `top-[8vh]`) gives way to it.
            'top-0 left-0 max-w-none translate-x-0 translate-y-0 sm:max-w-none',
            layout === 'full'
              ? 'h-[calc(100dvh-0.75rem)] max-h-none sm:max-h-none'
              : 'max-h-[calc(100dvh-2.5rem)] sm:max-h-[calc(100dvh-2.5rem)]',
          )}
          {...props}
        >
          {/* The handle: says the sheet can be swiped away. The swipe works
              anywhere on the sheet; the close button is the way without one. */}
          <span
            aria-hidden="true"
            className="bg-muted-foreground/40 pointer-events-none absolute top-1.5 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full"
          />
          {children}
          {showCloseButton ? (
            <DrawerPrimitive.Close
              data-slot={`${slot}-close`}
              render={
                <Button
                  variant="ghost"
                  className={cn('absolute', closeClassName)}
                  size="icon-sm"
                />
              }
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DrawerPrimitive.Close>
          ) : null}
        </DrawerPrimitive.Popup>
      </DrawerPrimitive.Viewport>
    </DrawerPrimitive.Portal>
  );
}

/**
 * The root a phone's dialog or sheet needs: Base UI's drawer, swiped down to
 * dismiss. Its props are the dialog root's; the drawer adds only the swipe.
 */
export function BottomSheetRoot({
  open,
  ...props
}: DrawerPrimitive.Root.Props) {
  // A sheet rendered already open — the task drawer, which exists only while the
  // address names a task — has no change from closed to open for Base UI to
  // animate, so it appeared in place while every other sheet rose (spec 266). It
  // opens one frame after it mounts instead, and rises like the rest.
  const [mountedOpen] = useState(open === true);
  const [risen, setRisen] = useState(!mountedOpen);
  useEffect(() => {
    if (risen) return undefined;
    const frame = requestAnimationFrame(() => {
      setRisen(true);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [risen]);
  return (
    <DrawerPrimitive.Root
      swipeDirection="down"
      {...props}
      open={risen ? open : false}
    />
  );
}
